import type {
  AiManagedGroupEligibleCampaign,
  AiManagedGroupPortfolioOption,
} from "@adpilot/shared";
import {
  IconArrowsSort,
  IconBriefcase2,
  IconFilterOff,
  IconRobot,
  IconSelector,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { CampaignStatusBadge } from "@/components/campaign/CampaignStatusBadge";
import { DataTableFooter } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export type CandidateDeliveryFilter = "all" | "enabled" | "paused" | "archived";
export type CandidatePortfolioFilter = "all" | "unassigned" | string;
export type CandidateAiFilter = "all" | "on" | "off";
export type CandidateSortField = "aiStatus" | "delivery";
export type CandidateSortDirection = "asc" | "desc";

interface ManagedGroupCandidatePickerProps {
  campaigns: AiManagedGroupEligibleCampaign[];
  portfolioOptions: AiManagedGroupPortfolioOption[];
  search: string;
  deliveryStatus: CandidateDeliveryFilter;
  portfolioId: CandidatePortfolioFilter;
  aiStatus: CandidateAiFilter;
  sortBy?: CandidateSortField;
  sortDirection?: CandidateSortDirection;
  checkedIds: Set<string>;
  page: number;
  pageSize: number;
  total: number;
  isLoading: boolean;
  isCampaignError: boolean;
  isPortfolioError: boolean;
  className?: string;
  title?: string;
  countLabel?: string;
  titleDescription?: string;
  layout?: "default" | "transfer";
  showFilters?: boolean;
  emptyText: string;
  onSearchChange: (value: string) => void;
  onDeliveryStatusChange: (value: CandidateDeliveryFilter) => void;
  onPortfolioIdChange: (value: CandidatePortfolioFilter) => void;
  onAiStatusChange: (value: CandidateAiFilter) => void;
  onSortChange?: (field: CandidateSortField) => void;
  onPageChange: (page: number) => void;
  onRetryCampaigns: () => void;
  onRetryPortfolios: () => void;
  onToggle: (campaign: AiManagedGroupEligibleCampaign) => void;
  onToggleAll: (checked: boolean, selectableCampaigns: AiManagedGroupEligibleCampaign[]) => void;
}

interface ManagedGroupCandidateFiltersProps {
  search: string;
  deliveryStatus: CandidateDeliveryFilter;
  portfolioId: CandidatePortfolioFilter;
  aiStatus: CandidateAiFilter;
  portfolioOptions: AiManagedGroupPortfolioOption[];
  countLabel?: string;
  layout?: "default" | "transfer";
  onSearchChange: (value: string) => void;
  onDeliveryStatusChange: (value: CandidateDeliveryFilter) => void;
  onPortfolioIdChange: (value: CandidatePortfolioFilter) => void;
  onAiStatusChange: (value: CandidateAiFilter) => void;
}

export function ManagedGroupCandidateFilters({
  search,
  deliveryStatus,
  portfolioId,
  aiStatus,
  portfolioOptions,
  countLabel,
  layout = "default",
  onSearchChange,
  onDeliveryStatusChange,
  onPortfolioIdChange,
  onAiStatusChange,
}: ManagedGroupCandidateFiltersProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const transfer = layout === "transfer";
  const portfolioItems = [
    {
      value: "all",
      label: transfer
        ? t("candidateFilters.allPortfoliosCompact")
        : t("candidateFilters.allPortfolios"),
    },
    { value: "unassigned", label: t("candidateFilters.noPortfolio") },
    ...portfolioOptions.map((option) => ({ value: option.id, label: option.name })),
  ];

  return (
    <div
      data-slot="campaign-candidate-filters"
      className={cn(
        "flex shrink-0 items-center gap-1.5 border-b border-line px-2.5 py-2",
        transfer && "h-[61px] gap-[7px] bg-table-footer py-0",
      )}
    >
      <SearchInput
        showIcon
        textSize="compact"
        className={cn("min-w-[108px] flex-1", transfer && "h-[30px]")}
        value={search}
        placeholder={t("create.searchCampaigns")}
        onChange={(event) => onSearchChange(event.target.value)}
      />
      <FilterSelect
        ariaLabel={t("create.deliveryFilter.label")}
        className={cn("w-[78px] min-w-0", transfer && "h-[30px] w-[91px]")}
        icon={transfer ? undefined : <IconSelector />}
        value={deliveryStatus}
        items={[
          { value: "all", label: t("create.deliveryFilter.all") },
          { value: "enabled", label: t("create.deliveryFilter.enabled") },
          { value: "paused", label: t("create.deliveryFilter.paused") },
          { value: "archived", label: t("create.deliveryFilter.archived") },
        ]}
        showAppliedState={!transfer}
        onValueChange={(value) => onDeliveryStatusChange(value as CandidateDeliveryFilter)}
      />
      <FilterSelect
        ariaLabel={t("candidateFilters.portfolio")}
        className={cn("w-[88px] min-w-0", transfer && "h-[30px] w-[116px]")}
        icon={transfer ? undefined : <IconBriefcase2 />}
        value={portfolioId}
        items={portfolioItems}
        showAppliedState={!transfer}
        onValueChange={onPortfolioIdChange}
      />
      <FilterSelect
        ariaLabel={t("candidateFilters.aiStatus")}
        className={cn("w-[78px] min-w-0", transfer && "h-[30px] w-[108px]")}
        icon={transfer ? undefined : <IconRobot />}
        value={aiStatus}
        items={[
          {
            value: "all",
            label: transfer
              ? t("candidateFilters.allAiStatesCompact")
              : t("candidateFilters.allAiStates"),
          },
          { value: "on", label: t("candidateFilters.aiOn") },
          { value: "off", label: t("candidateFilters.aiOff") },
        ]}
        showAppliedState={!transfer}
        onValueChange={(value) => onAiStatusChange(value as CandidateAiFilter)}
      />
      {countLabel ? (
        <p className={cn("text-[9px] leading-3 text-muted-foreground", transfer && "w-[68px]")}>
          {countLabel}
        </p>
      ) : null}
    </div>
  );
}

export function ManagedGroupCandidatePicker({
  campaigns,
  portfolioOptions,
  search,
  deliveryStatus,
  portfolioId,
  aiStatus,
  sortBy,
  sortDirection,
  checkedIds,
  page,
  pageSize,
  total,
  isLoading,
  isCampaignError,
  isPortfolioError,
  className,
  title,
  countLabel,
  titleDescription,
  layout = "default",
  showFilters = true,
  emptyText,
  onSearchChange,
  onDeliveryStatusChange,
  onPortfolioIdChange,
  onAiStatusChange,
  onSortChange,
  onPageChange,
  onRetryCampaigns,
  onRetryPortfolios,
  onToggle,
  onToggleAll,
}: ManagedGroupCandidatePickerProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const transfer = layout === "transfer";
  const selectableCampaigns = campaigns.filter((campaign) => campaign.status !== "archived");
  const allChecked =
    selectableCampaigns.length > 0 && selectableCampaigns.every((row) => checkedIds.has(row.id));
  const someChecked = selectableCampaigns.some((row) => checkedIds.has(row.id));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const hasActiveFilters =
    search.trim().length > 0 ||
    deliveryStatus !== "all" ||
    portfolioId !== "all" ||
    aiStatus !== "all";
  const hasLoadError = isCampaignError || isPortfolioError;
  const loadErrorMessage =
    isCampaignError && isPortfolioError
      ? t("candidateFilters.campaignAndPortfolioLoadFailed")
      : isCampaignError
        ? t("candidateFilters.campaignLoadFailed")
        : t("candidateFilters.portfolioLoadFailed");

  function retryFailedQueries(): void {
    if (isCampaignError) onRetryCampaigns();
    if (isPortfolioError) onRetryPortfolios();
  }

  return (
    <div
      data-pane="available"
      data-slot="managed-group-candidate-picker"
      className={cn("flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden", className)}
    >
      {title ? (
        <div
          className={cn(
            "flex h-10 shrink-0 items-center justify-between border-b border-line px-2.5",
            transfer && "h-[45px]",
          )}
        >
          <div className="flex min-w-0 flex-col gap-px">
            <p className="truncate text-[11px] leading-[14px] font-semibold">{title}</p>
            {titleDescription ? (
              <p className="truncate text-[9px] leading-3 text-muted-foreground">
                {titleDescription}
              </p>
            ) : null}
          </div>
          {!transfer ? (
            <p className="text-[9px] leading-3 text-muted-foreground">{countLabel}</p>
          ) : null}
        </div>
      ) : null}
      {showFilters ? (
        <ManagedGroupCandidateFilters
          search={search}
          deliveryStatus={deliveryStatus}
          portfolioId={portfolioId}
          aiStatus={aiStatus}
          portfolioOptions={portfolioOptions}
          onSearchChange={onSearchChange}
          onDeliveryStatusChange={onDeliveryStatusChange}
          onPortfolioIdChange={onPortfolioIdChange}
          onAiStatusChange={onAiStatusChange}
        />
      ) : null}

      <div
        className={cn(
          "flex h-[31px] shrink-0 items-center border-b border-line bg-subtle px-2.5 text-[9px] leading-3 font-medium text-muted-foreground",
          transfer && "h-6 gap-2.5 text-[8px] tracking-[0.04em] uppercase",
        )}
      >
        <span className={cn("w-[30px] shrink-0", transfer && "w-4")}>
          <Checkbox
            aria-label={t("create.selectAllAvailable")}
            checked={allChecked}
            indeterminate={!allChecked && someChecked}
            disabled={selectableCampaigns.length === 0}
            onCheckedChange={(checked) => onToggleAll(checked === true, selectableCampaigns)}
          />
        </span>
        <span className="min-w-0 flex-1">
          {t(transfer ? "create.columns.campaignPortfolio" : "create.columns.campaign")}
        </span>
        <ManagedGroupCandidateSortHeader
          className={cn("w-[70px]", transfer && "w-[64px]")}
          field="aiStatus"
          label={t("create.columns.aiStatus")}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <ManagedGroupCandidateSortHeader
          className={cn("w-[82px]", transfer && "w-[76px]")}
          field="delivery"
          label={t("create.columns.delivery")}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : hasLoadError ? (
          <ErrorState message={loadErrorMessage} onRetry={retryFailedQueries} />
        ) : campaigns.length ? (
          campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className={cn(
                "flex h-12 items-center border-b border-line px-2.5 transition-colors hover:bg-subtle/60",
                transfer && "gap-2.5",
              )}
            >
              <span className={cn("w-[30px] shrink-0", transfer && "w-4")}>
                <Checkbox
                  className={cn(transfer && "size-4")}
                  aria-label={t("create.selectForAdd", { name: campaign.name })}
                  checked={checkedIds.has(campaign.id)}
                  disabled={campaign.status === "archived"}
                  onCheckedChange={() => onToggle(campaign)}
                />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-px pr-2">
                <span className="truncate text-[11px] leading-[14px] font-semibold">
                  {campaign.name}
                </span>
                <span className="truncate text-[9px] leading-3 text-muted-foreground">
                  {campaign.portfolio?.name ?? t("candidateFilters.noPortfolio")} ·{" "}
                  {campaign.targetingType
                    ? t(`create.targeting.${campaign.targetingType}`)
                    : t("candidateFilters.unknownTargeting")}{" "}
                  {transfer
                    ? null
                    : `· ${campaign.aiManaged ? t("create.aiOn") : t("create.aiOff")}`}
                </span>
              </span>
              <span className={cn("w-[70px] shrink-0", transfer && "w-[64px]")}>
                <Badge
                  size="compact"
                  variant={campaign.aiManaged ? "success" : "neutral"}
                  className="font-normal"
                >
                  {campaign.aiManaged ? t("create.aiOnLabel") : t("create.aiOffLabel")}
                </Badge>
              </span>
              <span className={cn("w-[82px] shrink-0", transfer && "w-[76px]")}>
                <CampaignStatusBadge status={campaign.status} />
              </span>
            </div>
          ))
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-5 text-center text-xs text-muted-foreground">
            <p>{emptyText}</p>
            {hasActiveFilters ? (
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  onSearchChange("");
                  onDeliveryStatusChange("all");
                  onPortfolioIdChange("all");
                  onAiStatusChange("all");
                  onPageChange(1);
                }}
              >
                <IconFilterOff data-icon="inline-start" />
                {t("candidateFilters.clear")}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      <DataTableFooter className={cn("h-10 px-2.5", transfer && "h-[38px]")}>
        <DataTablePagination
          density="compact"
          page={page}
          pageCount={Math.max(1, Math.ceil(total / pageSize))}
          summary={t("create.pagination", { from, to, total })}
          onPageChange={onPageChange}
        />
      </DataTableFooter>
    </div>
  );
}

export function ManagedGroupCandidateSortHeader({
  className,
  field,
  label,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  className: string;
  field: CandidateSortField;
  label: string;
  sortBy?: CandidateSortField;
  sortDirection?: CandidateSortDirection;
  onSortChange?: (field: CandidateSortField) => void;
}): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const active = sortBy === field;
  const activeDirection = sortDirection ?? "asc";
  const SortIcon = active
    ? activeDirection === "desc"
      ? IconSortDescending
      : IconSortAscending
    : IconArrowsSort;
  const accessibleLabel = active
    ? t("create.sort.changeDirection", {
        label,
        current: t(`create.sort.${activeDirection}`),
        next: t(`create.sort.${activeDirection === "asc" ? "desc" : "asc"}`),
      })
    : t("create.sort.activate", { label });

  if (!onSortChange) return <span className={cn("shrink-0", className)}>{label}</span>;

  return (
    <button
      type="button"
      aria-label={accessibleLabel}
      aria-pressed={active}
      className={cn(
        "flex h-6 shrink-0 items-center gap-1 rounded px-1 text-left transition-colors hover:bg-control-highlight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active && "text-foreground",
        className,
      )}
      onClick={() => onSortChange(field)}
    >
      <span className="truncate">{label}</span>
      <SortIcon aria-hidden className="size-3 shrink-0" stroke={1.7} />
    </button>
  );
}
